// React
import { useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';

// Refine
import { useNotification, useTranslation } from '@refinedev/core';

// Mantine
import {
  alpha,
  Flex,
  Group,
  SegmentedControl,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import {
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton,
} from 'mantine-react-table';
import type {
  MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
} from 'mantine-react-table';

// Interfaces
import type { Rule } from '@billing/validation/rules';

// Column
import { RuleColumns } from 'columns/Rule';

// Components
import {
  BillingDataGridWrapper,
  ConfirmDialog,
  CreateRowButton,
  InternalRefreshButton,
} from 'components/common';
import { RuleEditModal } from './components/RuleEditModal';
import { RuleRowActions } from './components/RuleRowActions';
import { RuleCreateModal } from './components/RuleCreateModal';

// API
import {
  useActivateRuleVersion,
  useActivateLatestRuleVersion,
  useArchiveRuleVersion,
  useCreateRule,
  useCreateRuleVersion,
  useDeleteRuleSet,
  getRuleSetSnapshot,
  useListRulesBySite,
} from 'api/resource';
import { ruleBodySchema, ruleSchema } from '@billing/validation/rules';
import { useQueryClient } from '@tanstack/react-query';

// Hooks
import { useCanModify } from 'hooks';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

interface PendingEditedRuleVersion {
  sourceRuleID: string;
  name: string;
  body: Rule['body'];
  rule: Rule;
}

type RuleManagementRow = Rule & {
  hasNewerUnpublishedVersion?: boolean;
};

const compareRuleVersions = (left: Rule, right: Rule) => {
  if (right.version !== left.version) {
    return right.version - left.version;
  }

  return (
    new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  );
};

const buildRuleSetRowsWithPendingDrafts = (versions: Rule[]) => {
  const versionsByRuleSetID = new Map<string, Rule[]>();

  for (const version of versions) {
    const ruleSetVersions = versionsByRuleSetID.get(version.ruleSetID);
    if (ruleSetVersions) {
      ruleSetVersions.push(version);
      continue;
    }

    versionsByRuleSetID.set(version.ruleSetID, [version]);
  }

  return getRuleSetSnapshot(versions).map((ruleSetRow) => {
    const ruleSetVersions = versionsByRuleSetID.get(ruleSetRow.ruleSetID) || [];
    const newerUnpublishedVersion = ruleSetVersions
      .filter(
        (version) =>
          version.version > ruleSetRow.version &&
          version.status === 'draft' &&
          !version.active
      )
      .sort(compareRuleVersions)[0];

    return {
      ...ruleSetRow,
      hasNewerUnpublishedVersion: Boolean(newerUnpublishedVersion),
    } as RuleManagementRow;
  });
};

export const RuleManagement: React.FC = () => {
  const { translate } = useTranslation();
  const { open } = useNotification();
  const { canModify } = useCanModify('rules');
  const currentSite = useCurrentSiteStore();
  const currentUser = useCurrentUserStore();
  const queryClient = useQueryClient();
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('dark');
  const isDark = computedColorScheme === 'dark';
  const title = translate('pages:rules.pageTitle', 'Rule Management');
  useDocumentTitle(translate('pages:rules.documentTitle', 'Rules | QBilling'));
  const siteID = currentSite?.id;
  const userID = currentUser?.id;

  const [viewMode, setViewMode] = useState<'ruleSets' | 'versions'>('ruleSets');
  const pendingEditedVersion = useRef<PendingEditedRuleVersion | null>(null);
  const [highlightedDraftRuleID, setHighlightedDraftRuleID] = useState<
    string | null
  >(null);
  const [ruleSetPendingDelete, setRuleSetPendingDelete] = useState<Rule | null>(
    null
  );

  const columns = RuleColumns({
    onViewVersions: () => setViewMode('versions'),
  });

  const { data: versions = [], isLoading: isVersionsLoading } =
    useListRulesBySite({
      siteID,
    });

  const createRuleMutation = useCreateRule({ siteID });
  const createRuleVersionMutation = useCreateRuleVersion({ siteID });
  const activateRuleVersionMutation = useActivateRuleVersion({ siteID });
  const activateLatestRuleVersionMutation = useActivateLatestRuleVersion({
    siteID,
  });
  const archiveRuleVersionMutation = useArchiveRuleVersion({ siteID });
  const deleteRuleSetMutation = useDeleteRuleSet({ siteID });

  const ruleSets = buildRuleSetRowsWithPendingDrafts(versions);
  const rules = viewMode === 'ruleSets' ? ruleSets : versions;
  const isMutating =
    createRuleMutation.isPending ||
    createRuleVersionMutation.isPending ||
    activateRuleVersionMutation.isPending ||
    activateLatestRuleVersionMutation.isPending ||
    archiveRuleVersionMutation.isPending ||
    deleteRuleSetMutation.isPending;
  const isLoading = isVersionsLoading;

  const handleCreateRule = async ({
    values,
  }: {
    values: Record<string, unknown>;
  }): Promise<Rule> => {
    if (!canModify || !siteID || !userID)
      throw new Error('Rule creation context unavailable');
    const input = ruleSchema
      .omit({
        id: true,
        version: true,
        lastUpdatedBy: true,
        createdAt: true,
        lastUpdatedAt: true,
      })
      .parse({ ...values, siteID, createdBy: userID });
    return createRuleMutation.mutateAsync(input);
  };

  const notifyUserRequired = ({
    titleKey,
    fallbackTitle,
  }: {
    titleKey: string;
    fallbackTitle: string;
  }) => {
    open({
      type: 'error',
      message: translate(titleKey, fallbackTitle),
      description: translate(
        'pages:rules.notifications.currentUserRequired',
        'Current user is required.'
      ),
    });
  };

  const handleEditRule = async ({
    values,
    row,
    table,
  }: {
    values: Record<string, unknown>;
    row: MRT_Row<Rule>;
    table: MRT_TableInstance<Rule>;
  }): Promise<void> => {
    if (!userID) {
      notifyUserRequired({
        titleKey: 'pages:rules.notifications.updateFailedTitle',
        fallbackTitle: 'Rule Update Failed',
      });
      return;
    }

    const ruleBodyResult = ruleBodySchema.safeParse(values.body);

    if (!ruleBodyResult.success) {
      open({
        type: 'error',
        message: translate(
          'pages:rules.notifications.updateFailedTitle',
          'Rule Update Failed'
        ),
        description:
          ruleBodyResult.error.issues[0]?.message ||
          translate(
            'pages:rules.notifications.invalidExecutableRule',
            'Rule body is not a valid executable rule.'
          ),
      });
      return;
    }

    const name =
      (typeof values.name === 'string' && values.name.trim()) ||
      row.original.name;

    const pendingVersion = pendingEditedVersion.current;
    const canReuseVersion =
      pendingVersion?.sourceRuleID === row.original.id &&
      pendingVersion.name === name &&
      isEqual(pendingVersion.body, ruleBodyResult.data);
    const createdRule = canReuseVersion
      ? pendingVersion.rule
      : await createRuleVersionMutation.mutateAsync({
          ruleSetID: row.original.ruleSetID,
          lastUpdatedBy: userID,
          name,
          body: ruleBodyResult.data,
        });

    pendingEditedVersion.current = {
      sourceRuleID: row.original.id,
      name,
      body: ruleBodyResult.data,
      rule: createdRule,
    };
    const isRuleActive =
      row.original.active || row.original.status === 'published';
    const shouldActivate =
      typeof values.active === 'boolean' ? values.active : isRuleActive;
    if (shouldActivate) {
      await activateRuleVersionMutation.mutateAsync({
        id: createdRule.id,
        lastUpdatedBy: userID,
      });
    } else if (isRuleActive) {
      await archiveRuleVersionMutation.mutateAsync({
        id: row.original.id,
        lastUpdatedBy: userID,
      });
    }

    pendingEditedVersion.current = null;
    setHighlightedDraftRuleID(shouldActivate ? null : createdRule.id);
    table.setEditingRow(null);
  };

  const handleActivateRule = async (rule: Rule) => {
    if (!userID) {
      notifyUserRequired({
        titleKey: 'pages:rules.notifications.activationFailedTitle',
        fallbackTitle: 'Rule Activation Failed',
      });
      return;
    }

    await activateRuleVersionMutation.mutateAsync({
      id: rule.id,
      lastUpdatedBy: userID,
    });
  };

  const handleDeactivateRule = async (rule: Rule) => {
    if (!userID) {
      notifyUserRequired({
        titleKey: 'pages:rules.notifications.deactivationFailedTitle',
        fallbackTitle: 'Rule Deactivation Failed',
      });
      return;
    }

    await archiveRuleVersionMutation.mutateAsync({
      id: rule.id,
      lastUpdatedBy: userID,
    });
  };

  const handleActivateLatestVersion = (rule: Rule): void => {
    if (!canModify || isMutating) return;
    if (!userID) {
      notifyUserRequired({
        titleKey: 'pages:rules.notifications.activationFailedTitle',
        fallbackTitle: 'Rule Activation Failed',
      });
      return;
    }

    activateLatestRuleVersionMutation.mutate(
      { ruleSetID: rule.ruleSetID, lastUpdatedBy: userID },
      { onSuccess: () => setHighlightedDraftRuleID(null) }
    );
  };

  const deleteRuleSet = async (rule: Rule): Promise<void> => {
    await deleteRuleSetMutation.mutateAsync({ ruleSetID: rule.ruleSetID });
  };

  const handleDeleteRuleSet = async () => {
    if (!ruleSetPendingDelete) return;
    await deleteRuleSet(ruleSetPendingDelete);
    setRuleSetPendingDelete(null);
  };

  const tableOptions: MRT_TableOptions<Rule> = {
    data: rules,
    columns,
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableEditing: canModify,
    enableFullScreenToggle: false,
    state: {
      isLoading,
      showAlertBanner: false,
    },
    mantineTableBodyRowProps: ({ row }) => {
      const isHighlightedDraft = row.original.id === highlightedDraftRuleID;
      const isDraft = row.original.status === 'draft' && !row.original.active;
      const draftBackgroundColor = isDark
        ? alpha(theme.colors.yellow[6], 0.06)
        : alpha(theme.colors.yellow[1], 0.8);
      const highlightedDraftBackgroundColor = isDark
        ? alpha(theme.colors.yellow[4], 0.24)
        : alpha(theme.colors.yellow[2], 0.95);

      return {
        style: {
          ...(isDraft
            ? {
                backgroundColor: draftBackgroundColor,
              }
            : {}),
          ...(isHighlightedDraft
            ? {
                backgroundColor: highlightedDraftBackgroundColor,
                outline: `2px solid ${
                  isDark ? theme.colors.yellow[6] : theme.colors.yellow[7]
                }`,
                outlineOffset: '-2px',
              }
            : {}),
        },
      };
    },
    mantineCreateRowModalProps: {
      withCloseButton: !createRuleMutation.isPending,
      title: translate('pages:rules.modals.createTitle', 'Create Rule'),
      size: 'min(90rem, 96vw)',
      centered: true,
      closeOnClickOutside: false,
      closeOnEscape: !createRuleMutation.isPending,
      overlayProps: { backgroundOpacity: 0.45, blur: 4 },
    },
    mantineEditRowModalProps: {
      withCloseButton: true,
      title: translate('pages:rules.modals.editTitle', 'Edit Rule'),
      size: 'min(90rem, 96vw)',
      centered: true,
      closeOnClickOutside: true,
      overlayProps: {
        backgroundOpacity: 0.45,
        blur: 4,
      },
    },
    enableTopToolbar: true,
    onCreatingRowSave: handleCreateRule,
    onEditingRowSave: handleEditRule,
    renderToolbarInternalActions: ({ table }) => (
      <Flex align="center" gap="0.313rem">
        <InternalRefreshButton queryClient={queryClient} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
      </Flex>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Group gap="sm" wrap="wrap">
        <SegmentedControl
          value={viewMode}
          onChange={(value) => setViewMode(value as 'ruleSets' | 'versions')}
          data={[
            {
              label: translate(
                'pages:rules.viewModes.ruleSets',
                'Active Rule Sets'
              ),
              value: 'ruleSets',
            },
            {
              label: translate(
                'pages:rules.viewModes.allVersions',
                'All Versions'
              ),
              value: 'versions',
            },
          ]}
          size="sm"
        />
        {CreateRowButton<Rule>({
          table,
          type: 'rule',
          label: translate('pages:rules.addButton', 'Create Rule'),
          disabled: !canModify,
        })}
      </Group>
    ),
    renderCreateRowModalContent: ({ row, table }) => (
      <RuleCreateModal
        key={`${siteID}:${userID}:${row.id}`}
        row={row}
        table={table}
        siteID={siteID}
        userID={userID}
        timezone={currentSite.timezone}
        canCreate={Boolean(
          canModify &&
            ruleSchema.shape.siteID.safeParse(siteID).success &&
            ruleSchema.shape.createdBy.safeParse(userID).success &&
            currentSite.timezone
        )}
      />
    ),
    renderEditRowModalContent: ({ row, table }) => (
      <RuleEditModal
        row={row}
        table={table}
        isSaving={isMutating}
        timezone={currentSite.timezone}
        canModify={Boolean(
          canModify &&
            ruleSchema.shape.lastUpdatedBy.safeParse(userID).success &&
            currentSite.timezone
        )}
        onDelete={async () => {
          await deleteRuleSet(row.original);
          table.setEditingRow(null);
        }}
      />
    ),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 24,
        minSize: 24,
        maxSize: 24,
        Cell: ({ row, table }) => (
          <RuleRowActions
            row={row}
            canModify={canModify}
            isMutating={isMutating}
            onEdit={(targetRow) => table.setEditingRow(targetRow)}
            onActivate={handleActivateRule}
            onActivateLatestVersion={
              ruleSets.some(
                (ruleSet) =>
                  ruleSet.id === row.original.id &&
                  ruleSet.hasNewerUnpublishedVersion
              )
                ? handleActivateLatestVersion
                : undefined
            }
            onDeactivate={handleDeactivateRule}
            onDeleteRuleSet={(rule) => setRuleSetPendingDelete(rule)}
          />
        ),
      },
    },
  };

  return (
    <>
      <ConfirmDialog
        open={Boolean(ruleSetPendingDelete)}
        onClose={() => setRuleSetPendingDelete(null)}
        onConfirm={handleDeleteRuleSet}
        isLoading={deleteRuleSetMutation.isPending}
        title={translate(
          'pages:rules.modals.deleteRuleSetTitle',
          'Delete Rule Set'
        )}
        body={translate(
          'pages:rules.modals.deleteRuleSetBody',
          { name: ruleSetPendingDelete?.name ?? '' },
          'Are you sure you want to delete the entire rule set "{{name}}"? This will remove all versions in the set.'
        )}
        confirmText={translate('buttons.delete', 'Delete')}
      />

      <BillingDataGridWrapper
        title={title}
        tableOptions={tableOptions}
        dataTestID="rulesManagement"
        showAlertBanner={false}
      />
    </>
  );
};
